"use client";

import React from "react";
import SharedContactForm from "@/components/Forms/SharedContactForm";
import SectionTitle from "@/components/SectionTitle";

interface WhyChooseSectionProps {
  apptOptions?: { label: string; value: string }[];
  title?: string;
  subtitle?: string;
  description?: string;
  highlightsList?: { value: string; label: string; icon?: string }[];
}

export default function WhyChooseSection({
  apptOptions = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ],
  title = "VÌ SAO NÊN CHỌN ĐẦU TƯ DRAGON HOME NGAY HÔM NAY?",
  subtitle = "LỢI THẾ DỰ ÁN",
  description = "Dragon Home kiến tạo chuẩn sống mới tại Long An với mức giá đầu tư hấp dẫn, kết nối hạ tầng giao thông tương lai đột phá và chính sách bán hàng tối ưu, mở ra cơ hội kinh doanh sinh lời and an cư lâu dài vượt trội.",
  highlightsList = [
    {
      value: "25 Tr/m²",
      label: "Giá bán giai đoạn 1 hấp dẫn",
    },
    {
      value: "20 Triệu",
      label: "Booking sở hữu dễ dàng",
    },
    {
      value: "20%",
      label: "Thanh toán đợt 1 cực nhẹ",
    },
    {
      value: "5 Sao",
      label: "Hệ thống tiện ích chuẩn mực",
    },
  ],
}: WhyChooseSectionProps) {
  const defaultIcons = [
    // Icon 0: Money
    <svg
      className="w-8 h-8 text-third"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      key="money"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>,
    // Icon 1: Verify Check
    <svg
      className="w-8 h-8 text-third"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      key="check"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
      />
    </svg>,
    // Icon 2: Percent/Calendar
    <svg
      className="w-8 h-8 text-third"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      key="percent"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>,
    // Icon 3: Building/Stars
    <svg
      className="w-8 h-8 text-third"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      key="building"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>,
  ];

  const highlights = highlightsList.map((item, idx) => {
    const isCustomIcon = item.icon && typeof item.icon === "string";
    const iconElement = isCustomIcon ? (
      <img
        src={item.icon}
        alt={item.label}
        className="w-8 h-8 object-contain"
      />
    ) : (
      defaultIcons[idx % defaultIcons.length]
    );

    return {
      icon: iconElement,
      value: item.value,
      label: item.label,
    };
  });

  return (
    <section className="py-12 md:py-16 bg-transparent border-t border-black/5">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left Area (2/3 width = 8 cols) */}
          <div className="lg:col-span-7 space-y-6">
            <SectionTitle
              title={title}
              subtitle={subtitle}
              align="left"
              theme="green"
              subtitleColor="primaryHover"
              underlineColor="primaryHover"
            />
            <p className="text-xs text-black/65 font-medium leading-relaxed max-w-2xl text-justify">
              {description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-4">
              {highlights.map((hl, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 p-1 bg-white rounded-lg shadow-sm border border-black/5">
                    {hl.icon}
                  </div>
                  <div className="space-y-1">
                    <div className="text-xl font-extrabold text-gray-900">
                      {hl.value}
                    </div>
                    <p className="text-[11px] text-gray-500 font-medium leading-normal">
                      {hl.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Area (Form Card - 1/3 width = 5 cols) */}
          <div className="lg:col-span-5">
            <div className="w-full bg-white/70 backdrop-blur-xl border border-white/60 p-6 md:p-8 rounded-none shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)] relative text-gray-800">
              {/* Top decorative line */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/3 h-[3px] bg-third"></div>

              <div className="text-center mb-5">
                <p className="text-third text-[9px] font-bold uppercase tracking-[0.2em] mb-1.5">
                  Thông tin đặc quyền
                </p>
                <h4 className="text-lg md:text-xl font-bold text-neutral-900 uppercase tracking-wide">
                  ĐĂNG KÝ NHẬN THÔNG TIN
                </h4>
                <p className="text-gray-500 text-[11px] mt-1 font-light italic">
                  Thông tin bảo mật và được xử lý trong vòng 5 phút
                </p>
              </div>

              <SharedContactForm
                theme="light"
                apptOptions={apptOptions}
                submitButtonText="NHẬN BÁO GIÁ NGAY"
                nguon="Khối lý do chọn Dragon Home (WhyChooseSection)"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
